import { getApolloClient } from '@faustwp/core';
import { SEO } from '../../components';
import {
  Header,
  Footer,
  HeroSection,
  FinalCTASection,
} from '../../components/sections';
import {
  CareersIntroSection,
  CareersCultureSection,
  CareersOpenPositionsSection,
  CareersBenefitsSection,
  CareersProcessSection,
  CareersTeamSection,
} from '../../components/sections/careers';
import { fetchMeetOurTeamPosts } from '../../lib/blogUtils';
import { fetchHaileyJobAds } from '../../lib/positions/jobs';
import { fetchPageSettings } from '../../lib/queries/pageSettings';
import { mapRekrySettingsToCareersCopy } from '../../lib/rekryPageMapping';
import { CAREERS_COPY } from '../../constants/careersTranslations';
import { getCanonicalUrl, buildHreflangTags } from '../../lib/siteUrl';

const FALLBACK_FI = CAREERS_COPY.fi;

const DEFAULT_SEO_FI = {
  title: 'Rekry | Identio',
  description:
    'Älä tingi väreistäsi. Tule mukaan tiimiin. Identio on konsultointiin erikoistunut teknologiayritys, joka on 100% tekijöidensä omistama. Katso avoimet työpaikat ja mitä tarjoamme.',
};

const HAILEY_FALLBACK_DEFAULT = 'https://identio.careers.haileyhr.app/';

function resolveCareersFallbackUrl() {
  const fromEnv = process.env.HAILEYHR_CAREERS_FALLBACK_URL;
  if (fromEnv) return fromEnv;
  if (process.env.NODE_ENV === 'production') {
    throw new Error(
      '[rekry/index] HAILEYHR_CAREERS_FALLBACK_URL must be set in production'
    );
  }
  // Local dev / preview without the var set — use placeholder so the page still renders.
  return HAILEY_FALLBACK_DEFAULT;
}

export default function RekryPage({
  meetOurTeamPosts = [],
  positions = [],
  careersCopy = null,
  rekrySettings = null,
  careersFetchFailed,
  careersFallbackUrl,
}) {
  const copy = careersCopy ?? FALLBACK_FI;
  const hero = copy.hero ?? FALLBACK_FI.hero;
  const s = rekrySettings ?? {};
  const metaTitle = s.seoTitle ?? s.metaTitle ?? s.meta_title ?? DEFAULT_SEO_FI.title;
  const metaDescription = s.seoDescription ?? s.metaDescription ?? s.meta_description ?? DEFAULT_SEO_FI.description;

  return (
    <>
      <SEO
        title={metaTitle}
        description={metaDescription}
        url={getCanonicalUrl('/rekry')}
        alternates={buildHreflangTags('/rekry', '/careers')}
      />
      <Header />
      <main id="main-content" tabIndex={-1}>
        <HeroSection
          variant="careers"
          title={hero.title}
          description={hero.description}
          primaryCta={hero.primaryCta}
          primaryHref={hero.primaryHref}
          secondaryCta={hero.secondaryCta}
          secondaryHref={hero.secondaryHref}
        />
        <CareersIntroSection lang="fi" copy={copy} />
        <CareersCultureSection lang="fi" copy={copy} />
        <CareersOpenPositionsSection lang="fi" positions={positions} copy={copy} fallbackUrl={careersFallbackUrl} fetchFailed={careersFetchFailed}/>
        <CareersBenefitsSection lang="fi" copy={copy} />
        <CareersProcessSection lang="fi" copy={copy} />
        <CareersTeamSection posts={meetOurTeamPosts} lang="fi" copy={copy} />
        <FinalCTASection
          variant="careers"
          title={copy.finalCta?.title}
          body={copy.finalCta?.body}
          contactButtonText={copy.finalCta?.button}
          contactButtonUrl={copy.finalCta?.href}
        />
      </main>
      <Footer />
    </>
  );
}

export async function getStaticProps() {
  const client = getApolloClient();
  
  let meetOurTeamPosts = [];
  let positions = [];
  let careersFetchFailed = false;
  let careersCopy = null;
  let rekrySettings = null;
  try {
    rekrySettings = await fetchPageSettings(client, 'rekry');
    if (rekrySettings && Object.keys(rekrySettings).length > 0) {
      careersCopy = mapRekrySettingsToCareersCopy(rekrySettings);
    }
  } catch (e) {
    console.error('[rekry/index] config for rekrySettings failed with error: ', err);
    // ACF optional
  }
  
  // Fetch WP team posts and Hailey jobs in parallel. Each failure is isolated:
  // - WP failure → empty meetOurTeamPosts, but careersFetchFailed stays false
  // - Hailey failure → empty positions + careersFetchFailed = true (renders fallback card)
  const [teamResult, haileyResult] = await Promise.allSettled([
    fetchMeetOurTeamPosts(client),
    fetchHaileyJobAds(),
  ]);

  if (teamResult.status === 'fulfilled') {
    meetOurTeamPosts = teamResult.value ?? [];
  } else {
    console.error('[rekry/index] fetchMeetOurTeamPosts failed:', teamResult.reason);
  }

  if (haileyResult.status === 'fulfilled') {
    positions = haileyResult.value.positions;
    careersFetchFailed = haileyResult.value.failed;
  } else {
    // fetchHaileyJobAds is supposed to catch its own errors and return failed:true.
    // Reaching this branch means it threw unexpectedly — treat as failed.
    console.error('[rekry/index] fetchHaileyJobAds threw unexpectedly:', haileyResult.reason);
    careersFetchFailed = true;
  }
  
  return {
    props: {
      meetOurTeamPosts,
      positions,
      careersCopy,
      rekrySettings,
      careersFetchFailed,
      // Keep page-level revalidate aligned with `next: { revalidate: 600 }` in
      // lib/haileyhr/client.js so ISR and data cache invalidate together.
      careersFallbackUrl: resolveCareersFallbackUrl(),
    },
    revalidate: 600,
  };
}
